/**
 * Unlock prompt for Secure Fields. Opened either explicitly by a print page /
 * SecureField, or implicitly by `SecureUnlockGate` when the user navigates to
 * a route that requires unlock.
 *
 * Visual scope: single password field + Unlock button + helper text. No
 * setup flow here — `app/settings/organization.tsx` owns first-time setup
 * and rotation.
 */

import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { ActivityIndicator, Button, Modal, Text, TextInput } from '@/components/ui';
import { useSecureUnlock } from '@/services/secure-unlock-context';


export interface SecureUnlockModalProps {
	visible: boolean;
	onDismiss: () => void;
	/** Optional title — defaults to "Unlock sensitive data". */
	title?: string;
	/** Optional explanation rendered above the password field. */
	message?: string;
	/** Called after a successful unlock so callers can proceed with the gated action. */
	onUnlocked?: () => void;
}

export const SecureUnlockModal: React.FC<SecureUnlockModalProps> = ({
	visible,
	onDismiss,
	title = 'Unlock sensitive data',
	message,
	onUnlocked,
}) => {
	const { unlock, isReady, throttledUntil } = useSecureUnlock();
	const [password, setPassword] = useState('');
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!visible) {
			setPassword('');
			setError(null);
			setBusy(false);
		}
	}, [visible]);

	const throttleRemainingMs = Math.max(0, throttledUntil - Date.now());
	const isThrottled = throttleRemainingMs > 0;

	const handleSubmit = async () => {
		if (busy || isThrottled || !password) return;
		setBusy(true);
		setError(null);
		try {
			const ok = await unlock(password);
			if (ok) {
				onUnlocked?.();
				onDismiss();
			} else if (throttledUntil > Date.now()) {
				setError(`Too many attempts. Try again in ${Math.ceil(throttleRemainingMs / 1000)}s.`);
			} else {
				setError('Wrong password.');
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Could not unlock');
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			visible={visible}
			onDismiss={onDismiss}
			contentContainerStyle={styles.modal}
			onSubmit={handleSubmit}
		>
			<Text style={styles.title}>{title}</Text>
			{message ? <Text style={styles.message}>{message}</Text> : null}
			{!isReady ? (
				<Text style={styles.error}>
					No sensitive-data password is set up for this organization. An admin
					must configure one in Settings → Organization first.
				</Text>
			) : (
				<>
					<TextInput
						label="Password"
						value={password}
						onChangeText={setPassword}
						onSubmitEditing={handleSubmit}
						returnKeyType="go"
						secureTextEntry
						autoFocus
						autoCapitalize="none"
						autoCorrect={false}
						textContentType="password"
						editable={!busy && !isThrottled}
					/>
					{error ? <Text style={styles.error}>{error}</Text> : null}
					{isThrottled ? (
						<Text style={styles.error}>
							Locked for {Math.ceil(throttleRemainingMs / 1000)}s.
						</Text>
					) : null}
					<View style={styles.row}>
						<Button mode="outlined" onPress={onDismiss} disabled={busy}>Cancel</Button>
						<Button
							mode="contained"
							onPress={handleSubmit}
							disabled={busy || isThrottled || !password}
						>
							{busy ? <ActivityIndicator size="small" /> : 'Unlock'}
						</Button>
					</View>
				</>
			)}
		</Modal>
	);
};

const styles = StyleSheet.create({
	modal: { width: 420, maxWidth: '95%' },
	title: { fontSize: 18, fontWeight: '600', marginBottom: 8 },
	message: { marginBottom: 12, opacity: 0.8 },
	error: { color: '#b00020', marginTop: 8 },
	row: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
});
